import './UserModal.less';

import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Space,
} from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Carrier } from '../../models/Carrier';
import { User, UserRole } from '../../models/User';

interface IProps {
  carriers: Carrier[];
  selectedUser?: User;
  submitting?: boolean;
  error: Error | null;
  onClose: () => void;
  onSubmit: (user: User) => void;
}

const UserModal: React.FC<IProps> = ({
  carriers,
  error,
  onClose,
  onSubmit,
  selectedUser,
  submitting,
}) => {
  const [form] = Form.useForm<User>();
  const [changePassword, setChangePassword] = useState(false);
  const isEditing = !!selectedUser;
  const originalPasswordRef = useRef<string | undefined>();

  const closeModal = useCallback(() => {
    form.resetFields();
    setChangePassword(false);
    originalPasswordRef.current = undefined;
    onClose();
  }, [form, onClose]);

  const submitModal = (values: User) => {
    if (selectedUser) {
      values.id = selectedUser.id;
      if (!changePassword) {
        if (originalPasswordRef.current) {
          values.password = originalPasswordRef.current;
        }
        delete values.confirmPassword;
      } else {
        delete values.confirmPassword;
      }
    } else {
      delete values.confirmPassword;
    }
    onSubmit(values);
  };

  useEffect(() => {
    if (selectedUser) {
      originalPasswordRef.current = selectedUser.password;
      // don't set password fields when editing
      const { confirmPassword, password, ...userWithoutPassword } =
        selectedUser;
      form.setFieldsValue(userWithoutPassword);
    }
  }, [form, selectedUser]);

  useEffect(() => {
    if (error) {
      notification.error({
        description: error.message,
        message: error.name,
      });
    }
  }, [error]);

  const showPasswordFields = !isEditing || changePassword;

  return (
    <Modal
      centered
      destroyOnClose
      className="user-modal"
      footer={null}
      maskClosable={false}
      open={true}
      title={isEditing ? 'Update User' : 'Add User'}
      width={720}
      onCancel={closeModal}
    >
      <Form form={form} layout="vertical" onFinish={submitModal}>
        {/* Personal info */}
        <div className="user-modal__section">
          <div className="user-modal__section-title">Personal Info</div>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[{ message: 'First name is required', required: true }]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[{ message: 'Last name is required', required: true }]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { message: 'Email is required', required: true },
                  { message: 'Please enter a valid email', type: 'email' },
                ]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Divider className="user-modal__divider" />

        {/* Contact info */}
        <div className="user-modal__section">
          <div className="user-modal__section-title">Contact Info</div>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Phone" name="phone">
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Ext" name="ext">
                <Input placeholder="Ext" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Company" name="company">
                <Input placeholder="Enter company name" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Divider className="user-modal__divider" />

        {/* Account Settings */}
        <div className="user-modal__section">
          <div className="user-modal__section-title">Account Settings</div>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.role !== curr.role}
          >
            {() => (
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="Role"
                    name="role"
                    rules={[
                      { message: 'Please select a role', required: true },
                    ]}
                  >
                    <Select placeholder="Select role">
                      <Select.Option value={UserRole.SUPER_ADMIN}>
                        {UserRole.SUPER_ADMIN}
                      </Select.Option>
                      <Select.Option value={UserRole.ADMIN}>
                        {UserRole.ADMIN}
                      </Select.Option>
                      <Select.Option value={UserRole.CARRIER}>
                        {UserRole.CARRIER}
                      </Select.Option>
                      <Select.Option value={UserRole.CUSTOMER}>
                        {UserRole.CUSTOMER}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                {[UserRole.CARRIER, UserRole.CUSTOMER].includes(
                  form.getFieldValue('role')
                ) && (
                  <Col span={8}>
                    <Form.Item label="Carrier" name="carrierId">
                      <Select placeholder="Select carrier">
                        {carriers.map((carrier) => (
                          <Select.Option key={carrier.id} value={carrier.id}>
                            {carrier.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            )}
          </Form.Item>
        </div>

        <Divider className="user-modal__divider" />

        {/* Password */}
        <div className="user-modal__section">
          <div className="user-modal__section-header">
            <div className="user-modal__section-title">
              {isEditing ? 'Password' : 'Set Password'}
            </div>
            {isEditing && (
              <Checkbox
                checked={changePassword}
                onChange={(e) => {
                  setChangePassword(e.target.checked);
                  if (!e.target.checked) {
                    form.setFieldsValue({
                      confirmPassword: undefined,
                      password: undefined,
                    });
                  }
                }}
              >
                Change password
              </Checkbox>
            )}
          </div>

          {showPasswordFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  hasFeedback
                  label={isEditing ? 'New Password' : 'Password'}
                  name="password"
                  rules={[
                    { message: 'Password is required', required: true },
                    {
                      message: 'Password must be at least 8 characters',
                      min: 8,
                    },
                  ]}
                >
                  <Input.Password
                    autoComplete="new-password"
                    placeholder="Enter password"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  hasFeedback
                  dependencies={['password']}
                  label="Confirm Password"
                  name="confirmPassword"
                  rules={[
                    { message: 'Please confirm password', required: true },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject('Passwords do not match');
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    autoComplete="new-password"
                    placeholder="Confirm password"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {isEditing && !changePassword && (
            <div className="user-modal__password-hint">
              Check the box above to set a new password for this user.
            </div>
          )}
        </div>

        {/* Email Notification (new users) */}
        {!isEditing && (
          <>
            <Divider className="user-modal__divider" />
            <Form.Item
              className="user-modal__send-email"
              name="sendEmail"
              valuePropName="checked"
            >
              <Checkbox>
                Send the new user an email about their account
              </Checkbox>
            </Form.Item>
          </>
        )}

        {/* Actions */}
        <div className="user-modal__actions">
          <Space size="large">
            <Button onClick={closeModal}>Cancel</Button>
            <Button htmlType="submit" loading={submitting} type="primary">
              {isEditing ? 'Update User' : 'Add User'}
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default UserModal;
